// src/pages/EmployeeDocsApprovals.js
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { db } from "../firebase/firebase";
import {
  collectionGroup,
  onSnapshot,
  orderBy,
  query,
  where,
} from "firebase/firestore";

const statusBadge = (s) => {
  const st = String(s || "draft").toLowerCase();
  const styles = {
    padding: "4px 10px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 800,
    display: "inline-block",
  };
  if (st === "submitted") return <span style={{ ...styles, background: "#ecfeff", color: "#155e75", border: "1px solid #a5f3fc" }}>SUBMITTED</span>;
  if (st === "rejected") return <span style={{ ...styles, background: "#fff7ed", color: "#9a3412", border: "1px solid #fed7aa" }}>REJECTED</span>;
  if (st === "approved") return <span style={{ ...styles, background: "#ecfdf5", color: "#065f46", border: "1px solid #a7f3d0" }}>APPROVED</span>;
  return <span style={{ ...styles, background: "#f3f4f6", color: "#374151", border: "1px solid #e5e7eb" }}>DRAFT</span>;
};

function fmtTs(v) {
  if (!v) return "-";
  if (v?.seconds) return new Date(v.seconds * 1000).toLocaleString();
  const d = new Date(v);
  return isNaN(d.getTime()) ? "-" : d.toLocaleString();
}

export default function EmployeeDocsApprovals() {
  const navigate = useNavigate();
  const role = (localStorage.getItem("role") || "").toLowerCase();
  const canAccess = role === "admin" || role === "md";

  const [filter, setFilter] = useState("submitted"); // submitted | all
  const [rows, setRows] = useState([]);

  const q = useMemo(() => {
    const base = collectionGroup(db, "submissions");
    if (filter === "submitted") {
      return query(base, where("status", "==", "submitted"), orderBy("updatedAt", "desc"));
    }
    return query(base, orderBy("updatedAt", "desc"));
  }, [filter]);

  useEffect(() => {
    if (!canAccess) return;
    const unsub = onSnapshot(
      q,
      (snap) => {
        const list = snap.docs.map((d) => {
          const token = d.ref.parent?.parent?.id || "";
          return {
            id: d.id,
            token,
            codeHash: d.id,
            ...d.data(),
          };
        });
        setRows(list);
      },
      (err) => {
        console.error(err);
      }
    );
    return () => unsub();
  }, [q, canAccess]);

  if (!canAccess) {
    return (
      <div style={{ padding: 20 }}>
        <h2>Employee Document Approvals</h2>
        <div style={{ color: "#b91c1c", fontWeight: 700 }}>Not authorized.</div>
      </div>
    );
  }

  return (
    <div style={{ padding: 20 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
        <div>
          <h2 style={{ margin: 0 }}>Employee Document Approvals</h2>
          <div style={{ color: "#6b7280", fontSize: 13, marginTop: 4 }}>
            Drivers upload documents via a secure link + code. You review and approve/reject here.
          </div>
        </div>

        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <label style={{ fontSize: 13, color: "#374151", fontWeight: 700 }}>View:</label>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            style={{ padding: "8px 10px", borderRadius: 8, border: "1px solid #d1d5db" }}
          >
            <option value="submitted">Submitted only</option>
            <option value="all">All</option>
          </select>
        </div>
      </div>

      <div style={{ marginTop: 16, border: "1px solid #e5e7eb", borderRadius: 12, overflow: "hidden", background: "white" }}>
        <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr 0.9fr 1.2fr 0.6fr", gap: 12, padding: 12, background: "#f9fafb", borderBottom: "1px solid #e5e7eb", fontSize: 12, fontWeight: 900, color: "#374151" }}>
          <div>Employee</div>
          <div>Company</div>
          <div>Status</div>
          <div>Updated</div>
          <div></div>
        </div>

        {rows.length === 0 ? (
          <div style={{ padding: 14, color: "#6b7280" }}>No submissions found.</div>
        ) : (
          rows.map((r) => (
            <div
              key={`${r.token}_${r.codeHash}`}
              style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr 0.9fr 1.2fr 0.6fr", gap: 12, padding: 12, borderBottom: "1px solid #e5e7eb", alignItems: "center" }}
            >
              <div style={{ fontWeight: 800 }}>
                {r.driverName || r.fullName || r.driverId || "(unknown)"}
              </div>
              <div style={{ color: "#374151" }}>{r.company || "-"}</div>
              <div>{statusBadge(r.status)}</div>
              <div style={{ color: "#6b7280", fontSize: 13 }}>{fmtTs(r.updatedAt)}</div>
              <div style={{ textAlign: "right" }}>
                <button
                  onClick={() => navigate(`/employee-docs-approvals/${r.token}/${r.codeHash}`)}
                  style={{ padding: "8px 10px", borderRadius: 8, border: "1px solid #d1d5db", background: "#111827", color: "white", fontWeight: 800, cursor: "pointer" }}
                >
                  Open
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
